import { Alert, Badge, Button, cardClass, fieldClass, SearchField, selectClass, Table, textareaClass } from '@ledgerloom/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { propose } from '../api';
import { useApi, useDebounced, usePageTitle } from '../hooks';
import { Breadcrumb, Empty, ErrorBanner, Icon, ListSkeleton, Reasons, StateChip, Time, ToggleGroup, outcomeMessage, outcomeTone, useToast } from '../components/ui';
import { Field } from '../components/Field';
import { Tabs } from '../components/Tabs';
import { count } from '../format';

// The asset library (docs/plans/asset-library-plan.md): the email templates
// agents send instead of writing from scratch, what each version changed, and
// who or what approved it. Patterns: data-display/table and forms/search-field
// for the list, navigation/tabs for an asset, data-display/comparison-table to
// compare versions, data-display/timeline for the decisions. Template text is
// data: the preview is the engine's HTML in a sandboxed frame.

interface Who { kind: 'agent' | 'person' | 'system'; id: string | null; name: string }
interface AssetRow {
  id: string; kind: string; name: string; purpose: string; category: string; status: string; merged_into: string | null; current_version: number; source: string;
  current: { asset_version_id: string; version: number; subject: string; change_class: string; created_at: string };
  last_change: { proposal_id: string; by: Who; change_class: string | null; at: string };
  sent_30_days: number; last_sent_at: string | null; pending: number;
}
interface Rendered { subject: string; preheader: string; text: string; html: string; hidden_blocks: string[]; missing_slots: string[] }
interface Version {
  id: string; version: number; status: string; change_class: string; created_at: string; approval_proposal_id: string; proposed_by: Who;
  approved: { kind: 'person' | 'proposer' | 'rules'; people: Array<{ user_id: string; name: string }> };
}
interface AssetDetail extends AssetRow {
  current_detail: { merge_fields: Array<{ key: string; source: string; fallback?: string }>; content: { blocks: Array<{ id: string; type: string; text?: string; open?: boolean; locked?: boolean }> }; preview: Rendered };
  versions: Version[];
}
interface Decision {
  proposal_id: string; schema_id: string; state: string; rationale: string | null; created_at: string; proposed_by: Who; change_class: string | null; version: number | null;
  flagged: Array<{ rule_id: string; severity: string; reason: string }>; signoffs: Array<{ user_id: string; name: string; decision: string; note: string | null; at: string }>;
}
interface Pending { proposal_id: string; schema_id: string; asset_id: string | null; name: string; change_class: string | null; created_at: string; proposed_by: Who; waiting_on: string | null }

const CATEGORY: Record<string, string> = { one_to_one: 'One-to-one', marketing: 'Marketing', transactional: 'Transactional' };
const CHANGE: Record<string, string> = { new: 'New', cosmetic: 'Cosmetic', structural: 'Structural', content: 'Words changed' };
const APPROVED: Record<string, string> = { person: 'Approved by', proposer: 'Approved by its author,', rules: 'Passed the checks; no person needed' };
const STATUS: Record<string, string> = { approved: 'Approved', deprecated: 'Deprecated', merged: 'Merged', archived: 'Archived' };
const ACTION: Record<string, string> = { 'asset.create': 'New template', 'asset.version': 'New version', 'asset.status': 'Status change' };
const byText = (w: Who) => (w.kind === 'agent' ? `${w.name} (agent)` : w.name);

export function Library() {
  usePageTitle('Library');
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? 'approved';
  const [q, setQ] = useState('');
  const query = useDebounced(q);
  const path = `/v1/assets?status=${status === 'approved' ? 'approved' : status}${query ? `&q=${encodeURIComponent(query)}` : ''}`;
  const data = useApi<AssetRow[]>(path);
  const pending = useApi<Pending[]>('/v1/assets/pending');
  const [creating, setCreating] = useState(false);
  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Library</h1><p>Approved email templates agents send instead of writing from scratch. Agents may propose changes; anything that changes the words waits for a person.</p></div>
        <Button type="button" onClick={() => setCreating((c) => !c)} aria-expanded={creating} aria-controls="new-template"><Icon name="plus" />New template</Button>
      </div>
      {creating && <NewTemplate onDone={() => { setCreating(false); data.reload(); pending.reload(); }} />}
      {!!pending.data?.length && (
        <section className={cardClass} aria-labelledby="pending-h">
          <div className="card-head"><h2 id="pending-h">Waiting for a person</h2></div>
          <ul className="list">
            {pending.data.map((p) => (
              <li key={p.proposal_id} className="list-item">
                <div className="grow">
                  <Link to={`/activity/${p.proposal_id}`}>{ACTION[p.schema_id] ?? p.schema_id}: {p.name}</Link>
                  <div className="faint small">{[p.change_class && (CHANGE[p.change_class] ?? p.change_class), `from ${byText(p.proposed_by)}`, p.waiting_on && `waiting on ${p.waiting_on}`].filter(Boolean).join(' · ')} · <Time iso={p.created_at} /></div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="row">
        <SearchField id="asset-q" className="grow basis-56 max-w-[360px]" label="Search templates" placeholder="Search by name or purpose" value={q} onValueChange={setQ} />
        <ToggleGroup label="Show templates that are" value={status} options={[['approved', 'Approved'], ['deprecated', 'Deprecated'], ['all', 'All']]} onChange={(v) => setParams({ status: v }, { replace: true })} />
      </div>
      <ErrorBanner error={data.error} />
      <section className={cardClass} aria-label="Templates">
        {data.loading && !data.data ? <ListSkeleton rows={3} label="Loading templates" /> : !data.data?.length ? <Empty title={query ? 'No templates match' : 'No templates yet'} /> : (
          <Table label="Templates">
            <caption className="sr-only">Email templates</caption>
            <thead><tr><th scope="col">Template</th><th scope="col" className="hide-sm">Kind</th><th scope="col">Version</th><th scope="col" className="hide-sm">Last change</th><th scope="col" className="hide-sm">Sent (30 days)</th><th scope="col">Status</th></tr></thead>
            <tbody>{data.data.map((a) => (
              <tr key={a.id}>
                <td><Link to={`/library/${a.id}`}>{a.name}</Link><div className="faint">{a.purpose}</div></td>
                <td className="small hide-sm">{CATEGORY[a.category] ?? a.category}</td>
                <td>v{a.current_version}</td>
                <td className="small hide-sm">{a.last_change.change_class ? `${CHANGE[a.last_change.change_class]} · ` : ''}{byText(a.last_change.by)}<div className="faint"><Time iso={a.last_change.at} /></div></td>
                <td className="hide-sm">{count(a.sent_30_days)}</td>
                <td><Badge tone={a.status === 'approved' ? 'success' : 'neutral'}>{STATUS[a.status] ?? a.status}</Badge>{a.pending > 0 && <div className="small">{a.pending} waiting</div>}</td>
              </tr>
            ))}</tbody>
          </Table>
        )}
      </section>
    </div>
  );
}

// Paragraphs become text blocks; a paragraph in square brackets is an open block
// the sender fills. Fields the record holds are declared for the person.
const SOURCES = ['first_name', 'last_name', 'full_name', 'company', 'owner_name', 'owner_email', 'city', 'state'];
function templateFrom(subject: string, body: string) {
  const blocks = body.split(/\n\s*\n/).map((t) => t.trim()).filter(Boolean).map((t, i) => {
    const open = /^\[.*\]$/s.test(t);
    return { id: `b${i + 1}`, type: 'text', text: open ? t.slice(1, -1).trim() : t, ...(open ? { open: true } : {}) };
  });
  const used = [...new Set([...`${subject}\n${body}`.matchAll(/\{\{\s*([a-z_]+)\s*\}\}/g)].map((m) => m[1]!))];
  const merge_fields = used.filter((k) => SOURCES.includes(k)).map((k) => ({ key: k, source: k, ...(k === 'first_name' ? { fallback: 'there' } : {}) }));
  return { content: { subject, blocks }, merge_fields };
}

function NewTemplate({ onDone }: { onDone: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: '', purpose: '', category: 'one_to_one', subject: '', body: 'Hi {{first_name}},\n\n[What you want to say]\n\nBest,\n{{owner_name}}' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [reasons, setReasons] = useState<any[]>([]);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setReasons([]);
    try {
      const out = await propose('asset.create', { kind: 'email_template', name: f.name, purpose: f.purpose, category: f.category, ...templateFrom(f.subject, f.body) }, 'Added in the Library');
      toast(outcomeMessage(out, 'Template added'), outcomeTone(out));
      if (out.state === 'rejected') setReasons(out.reasons);
      else { onDone(); if (out.result?.asset_id) navigate(`/library/${out.result.asset_id}`); }
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  return (
    <form id="new-template" className={`${cardClass} card-pad stack`} onSubmit={submit} aria-labelledby="new-h">
      <h2 id="new-h">New template</h2>
      <ErrorBanner error={error} focusOnShow />
      {!!reasons.length && <Alert tone="danger" role="alert"><Reasons reasons={reasons} /></Alert>}
      <Field label="Name" required><input className={fieldClass} value={f.name} onChange={set('name')} maxLength={200} /></Field>
      <Field label="When to use it" hint="Agents choose templates by this sentence." required><input className={fieldClass} value={f.purpose} onChange={set('purpose')} maxLength={500} /></Field>
      <Field label="Kind of email"><select className={selectClass} value={f.category} onChange={set('category')}>{Object.entries(CATEGORY).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
      <Field label="Subject" required><input className={fieldClass} value={f.subject} onChange={set('subject')} maxLength={200} /></Field>
      <Field label="Body" hint="Leave a blank line between paragraphs. Put the part the sender writes each time in [square brackets]. {{first_name}}, {{company}}, and {{owner_name}} come from the contact's record.">
        <textarea className={textareaClass} rows={8} value={f.body} onChange={set('body')} />
      </Field>
      <div className="row"><Button type="submit" disabled={busy}>{busy ? 'Adding' : 'Add template'}</Button></div>
    </form>
  );
}

type DetailTab = 'preview' | 'versions' | 'why';

export function AssetView() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as DetailTab) || 'preview';
  const data = useApi<AssetDetail>(`/v1/assets/${id}`);
  usePageTitle(data.data?.name ?? 'Template');
  const a = data.data;
  return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/library', label: 'Library' }, { label: a?.name ?? 'Template' }]} />
      <ErrorBanner error={data.error} />
      {!a ? <ListSkeleton rows={4} label="Loading the template" /> : (
        <>
          <div className="page-head"><div>
            <h1>{a.name}</h1>
            <p>{a.purpose}</p>
            <p className="small">
              <Badge tone={a.status === 'approved' ? 'success' : 'neutral'}>{STATUS[a.status] ?? a.status}</Badge> {CATEGORY[a.category]} · version {a.current_version} · sent {count(a.sent_30_days)} in 30 days
              {a.source === 'pack' && ' · a Mazu starter'}{a.merged_into && <> · merged into <Link to={`/library/${a.merged_into}`}>another template</Link></>}
            </p>
          </div></div>
          <Tabs label="Template" value={tab} tabs={[['preview', 'Preview'], ['versions', 'Versions'], ['why', 'Why']]} onChange={(t) => setParams({ tab: t }, { replace: true })}>
            {tab === 'preview' && <Preview asset={a} />}
            {tab === 'versions' && <Versions asset={a} />}
            {tab === 'why' && <Why id={a.id} />}
          </Tabs>
        </>
      )}
    </div>
  );
}

function Preview({ asset }: { asset: AssetDetail }) {
  const [sample, setSample] = useState<'full' | 'empty'>('full');
  const [format, setFormat] = useState<'html' | 'text'>('html');
  const r = useApi<Rendered>(`/v1/assets/${asset.id}/versions/${asset.current_version}/render?sample=${sample}`);
  // A block is named by its text (an open block's text says what to write), not its id.
  const blocks = asset.current_detail.content.blocks;
  const texts = (pick: (b: typeof blocks[number]) => boolean | undefined) => blocks.filter(pick).map((b) => b.text?.trim() || `A ${b.type} block`);
  const open = texts((b) => b.open);
  const locked = texts((b) => b.locked);
  return (
    <section className={`${cardClass} card-pad stack`} aria-label="Preview">
      <div className="row" style={{ gap: 16 }}>
        <ToggleGroup label="Recipient" value={sample} options={[['full', 'A full record'], ['empty', 'An empty record']]} onChange={setSample} />
        <ToggleGroup label="Format" value={format} options={[['html', 'HTML'], ['text', 'Plain text']]} onChange={setFormat} />
      </div>
      <ErrorBanner error={r.error} />
      {r.data && (
        <>
          <p style={{ margin: 0 }}><strong>Subject:</strong> {r.data.subject}{r.data.preheader && <span className="faint"> · {r.data.preheader}</span>}</p>
          {format === 'html'
            ? <iframe title={`${asset.name} as an HTML email`} sandbox="" srcDoc={r.data.html} className="email-frame" />
            : <pre className="email-text">{r.data.text}</pre>}
        </>
      )}
      <dl className="facts wide">
        <dt>Filled from the record</dt><dd>{asset.current_detail.merge_fields.length ? asset.current_detail.merge_fields.map((m, i) => <span key={m.key}>{i ? '; ' : ''}<code>{`{{${m.key}}}`}</code> from the {m.source.replace(/_/g, ' ')}{m.fallback ? `, else "${m.fallback}"` : ''}</span>) : 'Nothing'}</dd>
        <dt>Written by the sender each time</dt><dd>{open.length ? <BlockList texts={open} /> : 'Nothing: the whole email is fixed'}</dd>
        <dt>Locked (a person changes it)</dt><dd>{locked.length ? <BlockList texts={locked} /> : 'Nothing'}</dd>
      </dl>
    </section>
  );
}

function BlockList({ texts }: { texts: string[] }) {
  return <ul className="plain-list">{texts.map((t, i) => <li key={i}>{t}</li>)}</ul>;
}

// Two versions side by side, paragraph by paragraph, for the full sample record.
function Versions({ asset }: { asset: AssetDetail }) {
  const latest = asset.current_version;
  const [left, setLeft] = useState(Math.max(1, latest - 1));
  const [right, setRight] = useState(latest);
  const a = useApi<Rendered>(`/v1/assets/${asset.id}/versions/${left}/render`);
  const b = useApi<Rendered>(`/v1/assets/${asset.id}/versions/${right}/render`);
  const paras = (r: Rendered | null) => (r ? [r.subject, ...r.text.split(/\n{2,}/)] : []);
  const pa = paras(a.data);
  const pb = paras(b.data);
  const options = asset.versions.map((v) => <option key={v.version} value={v.version}>Version {v.version}</option>);
  return (
    <div className="stack">
      <section className={cardClass}>
        <Table label="Versions">
          <caption className="sr-only">Versions of {asset.name}</caption>
          <thead><tr>{['Version', 'Change', 'Proposed by', 'Approval', 'When'].map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
          <tbody>{asset.versions.map((v) => (
            <tr key={v.id}>
              <td>v{v.version}{v.status === 'approved' && <> <Badge tone="success">Current</Badge></>}</td>
              <td>{CHANGE[v.change_class] ?? v.change_class}</td>
              <td className="small">{byText(v.proposed_by)}</td>
              <td className="small">{APPROVED[v.approved.kind]} {v.approved.people.map((p) => p.name).join(', ')}</td>
              <td className="small"><Link to={`/activity/${v.approval_proposal_id}`}><Time iso={v.created_at} /></Link></td>
            </tr>
          ))}</tbody>
        </Table>
      </section>
      {asset.versions.length > 1 && (
        <section className={`${cardClass} card-pad stack`} aria-labelledby="compare-h">
          <h2 id="compare-h">Compare</h2>
          <div className="row" style={{ gap: 16 }}>
            <Field label="From"><select className={selectClass} value={left} onChange={(e) => setLeft(Number(e.target.value))}>{options}</select></Field>
            <Field label="To"><select className={selectClass} value={right} onChange={(e) => setRight(Number(e.target.value))}>{options}</select></Field>
          </div>
          <Table label="Comparison" tableClassName="version-compare">
            <caption className="sr-only">Version {left} and version {right}, paragraph by paragraph</caption>
            <thead><tr><th scope="col">Version {left}</th><th scope="col">Version {right}</th></tr></thead>
            <tbody>{Array.from({ length: Math.max(pa.length, pb.length) }, (_, i) => {
              const same = pa[i] === pb[i];
              return (
                <tr key={i} className={same ? undefined : 'changed'}>
                  <td className="email-text">{pa[i] ?? ''}</td>
                  <td className="email-text">{!same && <><Badge tone="warning">Changed</Badge>{' '}</>}{pb[i] ?? ''}</td>
                </tr>
              );
            })}</tbody>
          </Table>
        </section>
      )}
    </div>
  );
}

function Why({ id }: { id: string }) {
  const data = useApi<Decision[]>(`/v1/assets/${id}/decisions`);
  if (data.error) return <ErrorBanner error={data.error} />;
  if (!data.data) return <ListSkeleton rows={3} label="Loading decisions" />;
  return (
    <section className={`${cardClass} card-pad`} aria-label="Decisions">
      <ol className="timeline">
        {data.data.map((d) => (
          <li key={d.proposal_id}>
            <div className="row" style={{ gap: 8 }}>
              <strong>{ACTION[d.schema_id] ?? d.schema_id}{d.version ? ` (v${d.version})` : ''}</strong>
              <StateChip state={d.state} />
              {d.change_class && <Badge>{CHANGE[d.change_class] ?? d.change_class}</Badge>}
              <span className="faint small"><Time iso={d.created_at} /></span>
            </div>
            <p className="small">Proposed by {byText(d.proposed_by)}{d.rationale ? `: ${d.rationale}` : ''}</p>
            {!!d.flagged.length && <ul className="small">{d.flagged.map((f) => <li key={f.rule_id}>{f.reason} <span className="faint">· {f.rule_id}</span></li>)}</ul>}
            {d.signoffs.map((s) => <p key={s.user_id} className="small">{s.decision === 'approve' ? 'Approved' : 'Rejected'} by {s.name} <Time iso={s.at} />{s.note ? `: ${s.note}` : ''}</p>)}
            <Link className="small" to={`/activity/${d.proposal_id}`}>The full decision trail</Link>
          </li>
        ))}
      </ol>
    </section>
  );
}
